import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  InputAdornment,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { CameraIcon } from '@heroicons/react/24/outline';
import Modal from '../Modal';
import { PrimaryButton, SecondaryButton } from '../ui';
import { useTheme } from '../../context/ThemeContext';
import { CURRENCY } from '../../config/currency';
import toast from 'react-hot-toast';

// --- Types ---

export interface PettyCashFormData {
  amount: number | '';
  reason: string;
  takenBy: string;
  convertToExpense: boolean;
}

export interface PettyCashModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PettyCashFormData) => Promise<void>;
  loading?: boolean;
}

// --- Constants ---

const getDefaultForm = (): PettyCashFormData => ({
  amount: '',
  reason: '',
  takenBy: '',
  convertToExpense: false,
});

// --- Component ---

const PettyCashModal: React.FC<PettyCashModalProps> = ({
  open,
  onClose,
  onSave,
  loading: externalLoading = false,
}) => {
  const { theme } = useTheme();
  const [form, setForm] = useState<PettyCashFormData>(getDefaultForm());
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when opening
  useEffect(() => {
    if (open) {
      setForm(getDefaultForm());
      setErrors({});
    }
  }, [open]);

  // --- Handlers ---

  const handleChange = (field: keyof PettyCashFormData, value: unknown) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.amount || Number(form.amount) <= 0) newErrors.amount = 'Enter a valid amount';
    if (!form.reason.trim()) newErrors.reason = 'Reason is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { userMessage?: string; message?: string } } };
      const data = axiosErr?.response?.data;
      const message = data?.userMessage || data?.message || 'Failed to record cash withdrawal';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // --- Shared style helpers ---

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.components?.input?.background || '#FFFFFF',
      borderRadius: '10px',
      '& fieldset': { borderColor: theme.components?.input?.border || '#DDD8D2' },
      '&:hover fieldset': { borderColor: theme.primary },
      '&.Mui-focused fieldset': { borderColor: theme.components?.input?.focus || theme.primary },
    },
    '& .MuiInputLabel-root': { color: theme.gray?.[500] || '#666' },
    '& .MuiInputLabel-root.Mui-focused': { color: theme.primary },
    '& .MuiInputBase-input': { color: theme.text },
  };

  // --- Render ---

  const footer = (
    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
      <SecondaryButton
        onClick={onClose}
        disabled={saving}
        sx={{ px: 3 }}
      >
        Cancel
      </SecondaryButton>
      <PrimaryButton
        onClick={handleSubmit}
        loading={saving}
        disabled={externalLoading}
        sx={{ px: 3 }}
      >
        Record Cash Out
      </PrimaryButton>
    </Box>
  );

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Petty Cash / Cash Out"
      subtitle="Record cash withdrawn from the register"
      maxWidth="sm"
      footer={footer}
      mobileFullScreen
    >
      <form onSubmit={handleSubmit} noValidate>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {/* Amount - large and prominent */}
          <TextField
            label="Amount *"
            type="number"
            value={form.amount}
            onChange={(e) => handleChange('amount', e.target.value ? Number(e.target.value) : '')}
            error={Boolean(errors.amount)}
            helperText={errors.amount}
            fullWidth
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography sx={{ fontWeight: 700, color: theme.primary, fontSize: '1.1rem' }}>
                    {CURRENCY.symbol}
                  </Typography>
                </InputAdornment>
              ),
              sx: { fontSize: '1.5rem', fontWeight: 700 },
            }}
            sx={{
              ...inputSx,
              '& .MuiOutlinedInput-root': {
                ...inputSx['& .MuiOutlinedInput-root'],
                minHeight: 64,
              },
            }}
            inputProps={{ min: 0, step: 0.01 }}
          />

          {/* Reason */}
          <TextField
            label="Reason *"
            value={form.reason}
            onChange={(e) => handleChange('reason', e.target.value)}
            error={Boolean(errors.reason)}
            helperText={errors.reason}
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            placeholder="e.g. Bought cleaning supplies"
            sx={inputSx}
          />

          {/* Taken By */}
          <TextField
            label="Taken By"
            value={form.takenBy}
            onChange={(e) => handleChange('takenBy', e.target.value)}
            fullWidth
            placeholder="e.g. Ramesh (optional)"
            sx={inputSx}
          />

          {/* Receipt photo placeholder */}
          <Button
            variant="outlined"
            startIcon={<CameraIcon style={{ width: 20, height: 20 }} />}
            style={{ color: 'var(--text-primary)', border: '1.5px dashed #C8C5BD' }} /* basil-todo */
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '10px',
              py: 1.5,
              minHeight: 44,
              '&:hover': {
                borderColor: theme.primary,
                color: theme.primary,
                backgroundColor: `${theme.primary}08`,
              },
              '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
            }}
            onClick={() => toast('Receipt photo upload coming soon', { icon: '\uD83D\uDCF7' })}
          >
            Attach Receipt
          </Button>

          {/* Convert to Expense checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={form.convertToExpense}
                onChange={(e) => handleChange('convertToExpense', e.target.checked)}
                sx={{
                  color: theme.gray?.[400] || '#999',
                  '&.Mui-checked': { color: theme.primary },
                }}
              />
            }
            label={
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                  Convert to Expense
                </Typography>
                <Typography variant="caption" sx={{ color: theme.gray?.[500] || '#666' }}>
                  Also record this as an expense entry for reporting
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', ml: 0 }}
          />
        </Box>
      </form>
    </Modal>
  );
};

export default PettyCashModal;
